<template>
  <div>
    <div class="row">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-body">
            <div class="mb-3">
              <label for="adjustment-type">Adjustment Type<span class="text-danger">*</span></label>
              <b-form-select
                id="adjustment-type"
                v-model="adjustment.type"
                :options="adjustmentTypeOptions"
                class="form-select rounded shadow-none"
                :class="{ 'is-invalid': !$v.adjustment.type.required && submitted }"
              >
              </b-form-select>
              <p class="small text-danger mb-0" v-if="!$v.adjustment.type.required && submitted">Adjustment Type is required</p>
            </div>
            <div class="mb-3">
              <label for="adjustment-reason">Reason<span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                v-model="adjustment.reason"
                id="adjustment-reason"
                placeholder="Enter Reason"
                maxlength="40"
                :class="{ 'is-invalid': !$v.adjustment.reason.required && submitted }"
              />
              <p class="small text-danger mb-0" v-if="!$v.adjustment.reason.required && submitted">Reason is required</p>
              <p class="small">{{ adjustment.reason.length }}/40 characters</p>
            </div>
            <div class="mb-3">
              <label for="adjustment-direction">Adjustment Direction<span class="text-danger">*</span></label>
              <b-form-select
                id="adjustment-direction"
                v-model="adjustment.direction"
                :options="adjustmentDirectionOptions"
                class="form-select rounded shadow-none"
                :class="{ 'is-invalid': !$v.adjustment.direction.required && submitted }"
              >
              </b-form-select>
              <p class="small text-danger mb-0" v-if="!$v.adjustment.direction.required && submitted">Adjustment Direction is required</p>
            </div>
            <div class="mb-3">
              <label for="adjustment-amount">Amount<span class="text-danger">*</span></label>
              <div class="input-group" id="adjustment-amount">
                <span class="input-group-text" id="basic-addon1">₱</span>
                <input
                  type="number"
                  class="form-control"
                  v-model="adjustment.amount"
                  :class="{ 'is-invalid': !$v.adjustment.amount.required && submitted }"
                />
              </div>
              <p class="small text-danger mb-0" v-if="!$v.adjustment.amount.required && submitted">Amount is required</p>
            </div>
            <div class="mb-5">
              <label for="adjustment-account">Account Number<span class="text-danger">*</span></label>
              <div class="row">
                <div class="col-md-9">
                    <input
                      disabled
                      type="text"
                      class="form-control me-4"
                      v-model="adjustment.accountNumber"
                      id="adjustment-account"
                      placeholder="Account Number"
                      :class="{ 'is-invalid': !$v.adjustment.accountNumber.required && submitted }"
                    />
                </div>
                <div class="col-md-3">
                    <button class="btn btn-secondary d-flex align-items-center justify-content-center w-100 p-2" @click="getAccountList()">
                      <i class="bx bx-search"></i>Search Merchant
                    </button>
                </div>
              </div>
              <p class="small text-danger mb-0" v-if="!$v.adjustment.accountNumber.required && submitted">Account Number is required</p>
              <label class="small fw-semibold mt-1">Wallet Balance: ₱{{ walletBalance | toCurrency }}</label>
            </div>

            <div class="d-flex align-items-center">
              <b-link
                class="fw-bold text-decoration-underline"
                @click="
                  adjustment.status = 'draft';
                  submitAdjustment();
                "
                >SAVE AS DRAFT</b-link
              >
              <div class="ms-auto">
                <button
                  v-if="adjustment.status == 'draft' || adjustment.id == 0" 
                  class="btn btn-primary me-3"
                  style="width: 170px"
                  @click="
                    adjustment.status = 'awaiting_lm';
                    submitAdjustment();
                  "
                >
                  Approval
                </button>
                <router-link :to="{ name: 'Adjustment Transaction Dashboard' }" class="tc-white">
                  <b-button variant="light" class="w-sm" type="button" style="width: 170px">Cancel</b-button>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- #region Account List Modal -->
    <b-modal v-model="showAccountListModal" title="Search Merchant" size="xl" hide-footer scrollable>
      <div class="row p-2">
        <div class="col-md-12 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Search Keyword">
              <form class="d-flex" @submit.prevent="">
                <b-form-group id="input-group-3" label-for="search-field">
                  <b-form-select
                    class="form-select rounded-0 rounded-start shadow-none w-fit"
                    id="search-field"
                    v-model="search.field"
                    :options="searchFieldOptions"
                  ></b-form-select>
                </b-form-group>

                <b-input-group>
                  <b-form-input class="rounded-0 rounded-end" v-model="search.keyword" @keyup.enter="isSearching=true; getAccountList()"></b-form-input>
                  <span class="input-group-text bg-primary tc-white"><i class="bx bx-search"></i></span>
                </b-input-group>
              </form>
            </b-form-group>
          </div>
        </div>
      </div>
      <!-- #region Table -->
      <div class="table-responsive">
        <div class="table align-middle table-nowrap">
          <b-table
            :items="kankolekAccountList"
            :fields="fields"
            responsive="sm"
            :current-page="currentPage"
            :sort-by.sync="orderBy"
            :sort-desc.sync="sortDesc"
            :filter-included-fields="filterOn"
            class="table-check"
            @sort-changed="sortList"
          >
            <template #cell(action)="data">
              <b-link
                :disabled="!data.item.hasValidToken || data.item.availableBalance == 0"
                :class="{ 'text-muted': !data.item.hasValidToken || data.item.availableBalance == 0}"
                @click="setSelectedAccount(data.item)"
                >Select</b-link
              >
            </template>

            <template #cell(token)="data">
              <div>{{ data.item.hasValidToken ? "Yes" : "No" }}</div>
            </template>

            <template #cell(account_status)="data">
              <div :class="{ 'text-success': data.item.status == 1, 'text-danger': data.item.status == 0 }">
                {{ data.item.status == 1 ? "Enabled" : "Disabled" }}
              </div>
            </template>
          </b-table>
        </div>
      </div>
      <div class="row g-0 text-center" v-if="kankolekAccountTotal == 0">
        <span>No data to display.</span>
      </div>
      <!-- #endregion Table -->

      <!-- #region Pagination -->
      <div class="row g-0 text-center text-sm-start" v-if="kankolekAccountTotal != 0 && kankolekAccountTotal > 10">
        <div class="col-sm-12">
          <div class="row">
            <div class="col">
              <div class="form-group row pull-left">
                <label for="staticEmail" class="col-sm-6 col-form-label">Display:</label>
                <div class="col-sm-6">
                  <b-form-group label-for="display-record">
                    <b-form-select
                      class="form-select shadow-none w-fit"
                      id="display-record"
                      v-model="perPage"
                      :options="pageOptions"
                      @change="getAccountList()"
                    ></b-form-select>
                  </b-form-group>
                </div>
              </div>
              <div class="dataTables_paginate paging_simple_numbers float-end" v-if="kankolekAccountTotal > perPage">
                <b-pagination
                  class="pagination-rounded"
                  v-model="currentPage"
                  @input="getAccountList()"
                  :total-rows="kankolekAccountTotal"
                  :per-page="perPage"
                ></b-pagination>
              </div>
            </div>
          </div>
        </div>
        <!-- end col -->
      </div>
      <!-- #endregion Pagination -->
    </b-modal>
    <!-- #endregion Account List Modal -->
  </div>
</template>

<script>
import { layoutMethods, peddlrWalletMethods, peddlrWalletComputed } from "@/state/helpers";
import { required } from "vuelidate/lib/validators";
import Swal from "sweetalert2";

export default {
  page: {
    title: "Adjusment Transaction Form",
  },
  validations: {
    adjustment: {
      type: { required },
      reason: { required },
      direction: { required },
      amount: { required },
      accountNumber: { required },
    },
  },
  data() {
    return {
      adjustmentId: 0,
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 20, 30],
      filter: null,
      filterOn: [],
      orderBy: "u.id",
      sort: "DESC",
      sortDesc: false,
      submitted: false,
      isSearching: false,
      adjustment: {
        id: 0,
        type: "",
        reason: "",
        direction: "",
        amount: "",
        accountNumber: "",
        status: "",
      },
      search: {
        field: "",
        keyword: "",
      },
      walletBalance: 0,
      showAccountListModal: false,
      action: "",
      adjustmentTypeOptions: [
        { text: "Select Adjustment Type", value: "" },
        { text: "Reporting Only", value: "reporting_only" },
        { text: "Wallet Adjustment", value: "wallet_adjustment" },
      ],
      adjustmentDirectionOptions: [
        { text: "Select Adjustment Direction", value: "" },
        { text: "Addition", value: "addition" },
        { text: "Deduction", value: "deduction" },
      ],
      fields: [
        { key: "action", sortable: false },
        { key: "kankolekAcctNumber", label: "Account Number", sortable: false },
        { key: "token", sortable: false },
        { key: "userId", sortable: false },
        { key: "merchantName", sortable: false },
        { key: "businessId", sortable: false },
        { key: "businessName", sortable: false },
        { key: "mobile_number", sortable: false },
        { key: "email", label: "Email Address", sortable: false },
        { key: "account_status", sortable: false },
      ],
      searchFieldOptions: [
        { text: "Search All", value: "" },
        { text: "Account Number", value: "u.acct_no" },
        { text: "User ID", value: "u.id" },
        { text: "Merchant Name", value: "u.name" },
        { text: "Business ID", value: "b.id" },
        { text: "Business Name", value: "b.name" },
        { text: "Mobile Number", value: "u.mobile_number" },
        { text: "Email Address", value: "u.email" },
      ],
    };
  },
  components: {},
  computed: {
    ...peddlrWalletComputed,
  },
  methods: {
    ...layoutMethods,
    ...peddlrWalletMethods,
    async submitAdjustment() {
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }
      
      this.action = this.adjustment.id == 0 ? "create" : "update";

      if (this.adjustment.status == "awaiting_lm") {
        Swal.fire({
          title: "Are you sure?",
          text: "You will no longer be able to edit this request",
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "##804997",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes",
          cancelButtonText: "No",
        }).then(async (result) => {
          if (result.isConfirmed) {
            await this.actionAdjustmentTransaction({ action: this.action, ...this.adjustment })
              .then(async () => {
                Swal.fire("Successfully Created!", `Adjustment Transaction has been ${this.action}d`, "success");
                this.submitted = false;
                this.$router.push({ name: "Adjustment Transaction Dashboard" });
              })
              .catch((error) => {
                Swal.fire("Create Failed!", `An error occured while creating adjustment transaction: ${error.data.message}`, "error");
              });
          }
        });
      } else {
        await this.actionAdjustmentTransaction({ action: this.action, ...this.adjustment })
          .then(async () => {
            Swal.fire("Successfully Saved Draft!", `Adjustment Transaction has been ${this.action}d`, "success");
            this.submitted = false;
            this.$router.push({ name: "Adjustment Transaction Dashboard" });
          })
          .catch((error) => {
            Swal.fire("Save Draft Failed!", `An error occured while saving draft adjustment transaction: ${error.data.message}`, "error");
          });
      }
    },
    getAdjustmentDetails() {},
    async getAccountList() {
      let params = {
        page: this.isSearching ? 1 : this.currentPage,
        rows: this.perPage,
        sort: this.sort,
        orderBy: this.orderBy,
        search: this.search,
      };
      await this.getKankolekAccountList(params).then(() => {
        this.isSearching = false;
        this.showAccountListModal = true;
      });
    },
    setSelectedAccount(item) {
      this.adjustment.accountNumber = item.kankolekAcctNumber;
      this.walletBalance = item.availableBalance;
      this.showAccountListModal = false;
    },
  },
  async mounted() {
    this.adjustment.id = this.$route.params.id;
    this.changeTitle({ title: "Adjustment Transaction Form" });
    this.changeBreadCrumb({
      breadCrumbs: [
        { text: "Peddlr", to: { name: "Home" } },
        { text: "Kankolek", to: { name: "" } },
        { text: "Adjusment Transaction", to: { name: "Adjustment Transaction Dashboard" } },
        { text: "Form", to: { name: "" } },
      ],
    });

    if (this.adjustment.id > 0) {
      await this.getAdjustmentTransactionList({
        search: {
          field: "a.id",
          keyword: this.adjustment.id, 
        },
      }).then(async () => {
        this.adjustment.type = this.kankolekAdjustmentsList[0].adjustment_type
        this.adjustment.reason = this.kankolekAdjustmentsList[0].reason
        this.adjustment.direction = this.kankolekAdjustmentsList[0].adjustment_direction
        this.adjustment.amount = this.kankolekAdjustmentsList[0].amount
        this.adjustment.accountNumber = this.kankolekAdjustmentsList[0].acct_no
        this.adjustment.status = this.kankolekAdjustmentsList[0].status
      });
    }
  },
};
</script>

<style scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

a.disabled {
  pointer-events: none;
}
</style>
